/**
 * Checkout — email typo suggestion (mailcheck-style).
 *
 * Sprawdza domain part adresu po `@` przeciw liście znanych domen/TLD'ków.
 * Jeśli wygląda na literówkę (Levenshtein distance ≤ 2 od znanego), pokazuje
 * **warning** (NIE error — `.co` to valid TLD Kolumbii, nie wolno blokować):
 *
 *   "Czy chodziło Ci o `kamil@gmail.com`?  [Tak, popraw]  ✕"
 *
 * Triggery:
 *  - `blur` na `#billing_email`
 *  - Po typo correction → trigger normalny validation (auto-capitalize ignoruje email)
 *
 * Anti-noise:
 *  - Per-value dismiss (jeśli user kliknął ✕ dla "kamil@gnail.com" — nie pokazuj
 *    znowu dla tej samej wartości w tej sesji)
 *  - Skip dla emaili które już są w whitelist (exact match w known domains)
 *
 * Nie blokuje submita — user może zignorować warning i zapłacić.
 */

/* Znane domeny (full match). PL market: WP, Onet, Interia, O2 dominują. */
const KNOWN_DOMAINS = [
    // PL providers
    'gmail.com', 'wp.pl', 'onet.pl', 'interia.pl', 'op.pl', 'o2.pl',
    'gazeta.pl', 'tlen.pl', 'poczta.fm', 'poczta.onet.pl', 'interia.eu',
    // International common
    'hotmail.com', 'outlook.com', 'yahoo.com', 'icloud.com', 'me.com',
    'live.com', 'live.pl', 'proton.me', 'protonmail.com', 'aol.com',
    'msn.com', 'mac.com', 'fastmail.com',
];

/* TLD typo map — bezpośrednie mapowanie typowych literówek na poprawkę.
 * Szybsze niż Levenshtein na pełnej liście TLDów + pokrywa edge cases
 * jak `.co` (też valid, ale w 95% przypadków PL user'a to typo od `.com`). */
const TLD_TYPOS = {
    'co':   'com',   // .co (Kolumbia) — bardzo często typo .com w PL
    'con':  'com',
    'cm':   'com',
    'comm': 'com',
    'cim':  'com',
    'cpm':  'com',
    'p':    'pl',
    'pll':  'pl',
    'lp':   'pl',
    'ply':  'pl',
    'netr': 'net',
    'ner':  'net',
    'orq':  'org',
    'orgg': 'org',
};

/**
 * Levenshtein distance — iteratywna implementacja (DP, O(m*n) space).
 * Wystarczająca dla krótkich stringów (domeny ≤ 30 znaków).
 */
function levenshtein(a, b) {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    const m = a.length, n = b.length;
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            dp[i][j] = Math.min(
                dp[i - 1][j] + 1,        // delete
                dp[i][j - 1] + 1,        // insert
                dp[i - 1][j - 1] + cost, // substitute
            );
        }
    }
    return dp[m][n];
}

/**
 * Suggest poprawkę dla domeny. Zwraca string z poprawioną domeną albo null.
 *
 * Strategia:
 *  1. TLD typo map → bezpośrednia poprawka (np. `.co` → `.com`)
 *  2. Whole domain Levenshtein ≤ 2 → znana domena
 */
function suggestDomain(domain) {
    if (!domain) return null;
    domain = domain.toLowerCase();

    /* Exact match — nic do poprawienia. */
    if (KNOWN_DOMAINS.includes(domain)) return null;

    /* Step 1: TLD typo. Wycinamy ostatnią część po ostatniej kropce. */
    const lastDot = domain.lastIndexOf('.');
    if (lastDot > 0) {
        const tld = domain.substring(lastDot + 1);
        const base = domain.substring(0, lastDot);
        if (TLD_TYPOS[tld]) {
            return base + '.' + TLD_TYPOS[tld];
        }
    }

    /* Step 2: Levenshtein vs whole known domain list. Wybieramy najmniejszą
     * odległość ≤ 2. Skip jeśli oryginalny domain jest bardzo krótki
     * (np. "g.pl" — krótkie stringi dają false positives). */
    if (domain.length < 5) return null;
    let best = null, bestDist = Infinity;
    for (const known of KNOWN_DOMAINS) {
        const d = levenshtein(domain, known);
        if (d > 0 && d <= 2 && d < bestDist) {
            best = known;
            bestDist = d;
        }
    }
    return best;
}

/**
 * Parse email → { local, domain } albo null.
 */
function parseEmail(email) {
    if (!email) return null;
    const m = email.trim().match(/^(.+)@([^@\s]+)$/);
    if (!m) return null;
    return { local: m[1], domain: m[2] };
}

/* Per-session dismissals — jeśli user już machnął ręką na konkretne literówki,
 * nie pokazujemy znowu (anti-noise). */
const dismissed = new Set();

function ensureWarnEl(input) {
    let warn = input.parentElement.querySelector('.hn-co__email-warn');
    if (warn) return warn;
    warn = document.createElement('span');
    warn.className = 'hn-co__email-warn';
    warn.setAttribute('role', 'status');
    warn.hidden = true;
    /* Wstawiamy po error spanie (jeśli istnieje) lub na końcu fielda. */
    const errorSpan = input.parentElement.querySelector(`[data-error-for="${input.name}"]`);
    if (errorSpan) errorSpan.parentNode.insertBefore(warn, errorSpan.nextSibling);
    else input.parentElement.appendChild(warn);
    return warn;
}

function showSuggestion(input, suggestion) {
    const warn = ensureWarnEl(input);
    warn.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
        <span>Czy chodziło Ci o <button type="button" class="hn-co__email-warn-fix">${suggestion}</button>?</span>
        <button type="button" class="hn-co__email-warn-dismiss" aria-label="Zamknij">✕</button>
    `;
    warn.hidden = false;
}

function hideSuggestion(input) {
    const warn = input.parentElement.querySelector('.hn-co__email-warn');
    if (warn) warn.hidden = true;
}

function checkAndWarn(input) {
    const parsed = parseEmail(input.value);
    if (!parsed) { hideSuggestion(input); return; }

    const suggested = suggestDomain(parsed.domain);
    if (!suggested) { hideSuggestion(input); return; }

    const fullSuggestion = parsed.local + '@' + suggested;
    if (dismissed.has(fullSuggestion)) { hideSuggestion(input); return; }

    showSuggestion(input, fullSuggestion);

    /* Bindings — delegacja na warn element. */
    const warn = input.parentElement.querySelector('.hn-co__email-warn');
    if (!warn) return;
    warn.onclick = (e) => {
        const fix = e.target.closest('.hn-co__email-warn-fix');
        const dismiss = e.target.closest('.hn-co__email-warn-dismiss');
        if (fix) {
            input.value = fix.textContent;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
            hideSuggestion(input);
        } else if (dismiss) {
            dismissed.add(fullSuggestion);
            hideSuggestion(input);
        }
    };
}

export function initCheckoutEmailTypoCheck() {
    const input = document.getElementById('billing_email');
    if (!input) return;
    input.addEventListener('blur', () => checkAndWarn(input));
}
